import { useSyncExternalStore } from 'react'

export type SidebarFeatureNode = {
  key: string
  title: string
  children?: SidebarFeatureNode[]
}

export type SidebarFeatureGroup = {
  label: string
  items: SidebarFeatureNode[]
}

type FeatureNavItem = {
  title: string
  url?: string
  items?: readonly { title: string; url: string }[]
}

const EMPTY_HIDDEN: ReadonlySet<string> = new Set()
const STORAGE_PREFIX = 'ellan.ui-hidden-sidebar-features:'

let hiddenSnapshot: { scope: string; raw: string; value: ReadonlySet<string> } = {
  scope: '',
  raw: '',
  value: EMPTY_HIDDEN,
}
const hiddenListeners = new Set<() => void>()

let catalogSnapshot: SidebarFeatureGroup[] = []
const catalogListeners = new Set<() => void>()

const storageKey = (scope: string) => `${STORAGE_PREFIX}${scope || 'local'}`

export function sidebarFeatureKey(title: string, url?: string) {
  if (!url) {
    return `menu:${title}`
  }

  const [path, search = ''] = url.split('?')
  const params = new URLSearchParams(search)
  params.delete('service_instance_id')
  const rest = params.toString()
  return rest ? `path:${path}?${rest}` : `path:${path}`
}

export function applySidebarFeatureVisibility<T extends FeatureNavItem>(
  items: readonly T[],
  hidden: ReadonlySet<string>,
): T[] {
  return items.flatMap((item) => {
    if (hidden.has(sidebarFeatureKey(item.title, item.url))) {
      return []
    }

    if (!item.items) {
      return [item]
    }

    const children = item.items.filter((child) => !hidden.has(sidebarFeatureKey(child.title, child.url)))
    if (children.length === 0) {
      return []
    }

    return [{ ...item, items: children } as T]
  })
}

export function collectSidebarFeatures(
  items: readonly FeatureNavItem[],
  seen: Set<string>,
): SidebarFeatureNode[] {
  const nodes: SidebarFeatureNode[] = []

  for (const item of items) {
    const key = sidebarFeatureKey(item.title, item.url)
    const children = item.items?.flatMap((child) => {
      const childKey = sidebarFeatureKey(child.title, child.url)
      if (seen.has(childKey)) {
        return []
      }
      seen.add(childKey)
      return [{ key: childKey, title: child.title }]
    })

    if (seen.has(key)) {
      continue
    }

    if (item.items && (!children || children.length === 0)) {
      continue
    }

    seen.add(key)
    nodes.push(children && children.length > 0
      ? { key, title: item.title, children }
      : { key, title: item.title })
  }

  return nodes
}

function readHidden(scope: string): ReadonlySet<string> {
  if (typeof window === 'undefined') {
    return EMPTY_HIDDEN
  }

  const raw = window.localStorage.getItem(storageKey(scope)) ?? ''
  if (hiddenSnapshot.scope === scope && hiddenSnapshot.raw === raw) {
    return hiddenSnapshot.value
  }

  let keys: string[] = []
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) {
        keys = parsed.filter((item): item is string => typeof item === 'string')
      }
    } catch {
      keys = []
    }
  }

  const value = keys.length > 0 ? new Set(keys) : EMPTY_HIDDEN
  hiddenSnapshot = { scope, raw, value }
  return value
}

export function subscribeHiddenSidebarFeatures(onStoreChange: () => void) {
  hiddenListeners.add(onStoreChange)
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(STORAGE_PREFIX)) {
      hiddenSnapshot = { scope: '', raw: '', value: EMPTY_HIDDEN }
      onStoreChange()
    }
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage)
  }
  return () => {
    hiddenListeners.delete(onStoreChange)
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage)
    }
  }
}

export function setSidebarFeatureHidden(scope: string, key: string, hidden: boolean) {
  const next = new Set(readHidden(scope))
  if (hidden) {
    next.add(key)
  } else {
    next.delete(key)
  }

  const raw = JSON.stringify([...next])
  window.localStorage.setItem(storageKey(scope), raw)
  hiddenSnapshot = { scope, raw, value: next.size > 0 ? next : EMPTY_HIDDEN }
  hiddenListeners.forEach((listener) => listener())
}

export function useHiddenSidebarFeatures(scope: string) {
  return useSyncExternalStore(
    subscribeHiddenSidebarFeatures,
    () => readHidden(scope),
    () => EMPTY_HIDDEN,
  )
}

function catalogSignature(groups: readonly SidebarFeatureGroup[]) {
  return groups
    .map((group) => `${group.label}:${group.items.map((item) => `${item.key}/${item.children?.map((child) => child.key).join(',') ?? ''}`).join('|')}`)
    .join('||')
}

export function publishSidebarFeatureCatalog(groups: SidebarFeatureGroup[]) {
  if (catalogSignature(catalogSnapshot) === catalogSignature(groups)) {
    return
  }
  catalogSnapshot = groups
  catalogListeners.forEach((listener) => listener())
}

export function useSidebarFeatureCatalog() {
  return useSyncExternalStore(
    (onStoreChange) => {
      catalogListeners.add(onStoreChange)
      return () => catalogListeners.delete(onStoreChange)
    },
    () => catalogSnapshot,
    () => catalogSnapshot,
  )
}
